import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteSweep as DeleteSweepIcon,
  LockOpen as LockOpenIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import messageService from '../services/messageService';
import MessageList from '../components/messages/MessageList';
import MessageViewer from '../components/messages/MessageViewer';
import MessageComposer, { ComposeMode, type InitialComposeRecipient } from '../components/messages/MessageComposer';
import { MessageDetail } from '../types';
import { getDraft, type LocalDraftMessage } from '../services/draftMessageStore';
import { useAuth } from '../hooks/useAuth';
import ResponsiveScrollTabs from '../components/layout/ResponsiveScrollTabs';
import { isNativeShell } from '../utils/distribution';
import { useNativeCompactLandscape } from '../hooks/useNativeCompactLandscape';
import IconButton from '@mui/material/IconButton';

type ViewMode = 'list' | 'view' | 'compose';
type FolderType = 'inbox' | 'sent' | 'credits' | 'debits' | 'archive' | 'trash';

const Messages: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isMinor } = useAuth();
  const queryClient = useQueryClient();
  const [folder, setFolder] = useState<FolderType>('inbox');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedMailId, setSelectedMailId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<MessageDetail | undefined>(undefined);
  const [selectedDraft, setSelectedDraft] = useState<LocalDraftMessage | null>(null);
  const [composeMode, setComposeMode] = useState<ComposeMode | undefined>(undefined);
  const [initialComposeRecipients, setInitialComposeRecipients] = useState<InitialComposeRecipient[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Empty trash dialog state
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [emptyTrashLoading, setEmptyTrashLoading] = useState(false);

  // Release message reserves (postage + undelivered attached funds)
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseResult, setReleaseResult] = useState<{ expiredCount: number; releasedAmount: number } | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const { data: releasableReserves, refetch: refetchReleasable } = useQuery({
    queryKey: ['releasableMessageReserves', user?.username],
    queryFn: () => messageService.getReleasableReserves(),
    staleTime: 15_000,
    refetchInterval: 60_000,
    enabled: !!user?.username && !isMinor,
  });

  const releasableAmount = releasableReserves?.releasableAmount ?? 0;
  const pendingMessageCount = releasableReserves?.pendingMessageCount ?? 0;

  const handleReleaseReserves = useCallback(async () => {
    if (isMinor) {
      setReleaseError('Bulk reserve release is managed by a parent account.');
      return;
    }
    setReleaseLoading(true);
    setReleaseError(null);
    try {
      const result = await messageService.releaseAllReserves();
      if (result.success) {
        setReleaseResult({ expiredCount: result.expiredCount, releasedAmount: result.releasedAmount });
        queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
        await refetchReleasable();
        setRefreshKey((prev) => prev + 1);
      } else {
        setReleaseError('Failed to release reserves');
      }
    } catch (err: any) {
      setReleaseError(err?.message || 'Failed to release reserves');
    } finally {
      setReleaseLoading(false);
    }
  }, [isMinor, queryClient, refetchReleasable, user?.username]);

  useEffect(() => {
    if (isMinor && (folder === 'archive' || folder === 'trash')) {
      setFolder('inbox');
      setViewMode('list');
      setSelectedMailId(null);
    }
  }, [isMinor, folder]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldCompose = params.get('compose') === '1';
    const toAddress = (params.get('to') || '').trim();
    if (!shouldCompose || !toAddress) return;

    const displayName = (params.get('name') || toAddress).trim();
    const maxPay = Number(params.get('maxPay') || '0');
    const required = Number(params.get('required') || '0');

    setReplyTo(undefined);
    setSelectedDraft(null);
    setComposeMode(undefined);
    setSelectedMailId(null);
    setInitialComposeRecipients([{
      address: toAddress,
      displayName,
      maxPay: Number.isFinite(maxPay) ? maxPay : 0,
      deliveryFeeRequired: Number.isFinite(required) ? required : 0,
      type: 'to',
    }]);
    setViewMode('compose');

    navigate('/messages', { replace: true });
  }, [location.search, navigate]);

  const handleFolderChange = (newValue: FolderType) => {
    // If clicking the same folder tab, always go back to list view
    if (newValue === folder) {
      setViewMode('list');
      setSelectedMailId(null);
      setRefreshKey(prev => prev + 1);
    } else {
      // Switching to a different folder
      setFolder(newValue);
      setViewMode('list');
      setSelectedMailId(null);
      setRefreshKey(prev => prev + 1);
    }
  };

  const folderTabs = useMemo(() => {
    const tabs = [
      { value: 'inbox', label: 'Inbox', shortLabel: 'Inbox' },
      { value: 'sent', label: 'Sent', shortLabel: 'Sent' },
      { value: 'credits', label: 'Credits / Ad Revenue', shortLabel: 'Credits' },
      { value: 'debits', label: 'Debits / Purchases', shortLabel: 'Debits' },
    ];
    if (!isMinor) {
      tabs.push(
        { value: 'archive', label: 'Archive', shortLabel: 'Archive' },
        { value: 'trash', label: 'Trash', shortLabel: 'Trash' },
      );
    }
    return tabs;
  }, [isMinor]);

  const nativeShell = isNativeShell();
  const compactLandscape = useNativeCompactLandscape();

  const handleMessageSelect = (mailId: string) => {
    if (mailId.startsWith('draft:')) {
      const draftId = mailId.slice('draft:'.length);
      const draft = user?.username ? getDraft(user.username, draftId) : null;
      if (draft) {
        setReplyTo(undefined);
        setComposeMode(undefined);
        setSelectedDraft(draft);
        setViewMode('compose');
        return;
      }
    }

    setSelectedMailId(mailId);
    setSelectedDraft(null);
    setViewMode('view');
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedMailId(null);
    setReplyTo(undefined);
    setSelectedDraft(null);
    setComposeMode(undefined);
    // Refresh the list to update read status
    setRefreshKey(prev => prev + 1);
    void refetchReleasable();
  };

  const handleCompose = () => {
    setReplyTo(undefined);
    setSelectedDraft(null);
    setComposeMode(undefined);
    setInitialComposeRecipients([]);
    setViewMode('compose');
  };

  const handleReply = (message: MessageDetail) => {
    setReplyTo(message);
    setComposeMode('reply');
    setViewMode('compose');
  };

  const handleReplyAll = (message: MessageDetail) => {
    setReplyTo(message);
    setComposeMode('replyAll');
    setViewMode('compose');
  };

  const handleForward = (message: MessageDetail) => {
    setReplyTo(message);
    setComposeMode('forward');
    setViewMode('compose');
  };

  const handleDelete = (_mailId: string) => {
    setViewMode('list');
    setSelectedMailId(null);
    // Refresh the list to remove the deleted message
    setRefreshKey(prev => prev + 1);
  };

  const handleSent = () => {
    // Switch to sent folder and refresh to show the new message
    setFolder('sent');
    setSelectedDraft(null);
    setViewMode('list');
    setRefreshKey(prev => prev + 1);
    void refetchReleasable();
  };

  const handleDraftSaved = () => {
    if (folder === 'sent') {
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleEmptyTrash = async () => {
    setEmptyTrashLoading(true);
    try {
      const result = await messageService.emptyTrash();
      console.log('Empty trash result:', result);
      setEmptyTrashDialogOpen(false);
      setRefreshKey(prev => prev + 1);
    } catch (err: any) {
      console.error('Failed to empty trash:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      alert(`Failed to empty trash: ${errorMessage}`);
    } finally {
      setEmptyTrashLoading(false);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        mt: nativeShell ? 0 : { xs: 1, sm: 2, md: 3 },
        mb: nativeShell ? 0 : { xs: 1, sm: 2, md: 3 },
        px: nativeShell ? 0 : { xs: 0, sm: 1 },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header with Tabs and Compose Button */}
        <Paper
          elevation={compactLandscape ? 0 : 1}
          sx={{
            mb: compactLandscape ? 0 : { xs: 0.5, sm: 2 },
            flexShrink: 0,
            borderRadius: compactLandscape ? 0 : undefined,
            position: compactLandscape ? 'relative' : undefined,
            zIndex: compactLandscape ? 2 : undefined,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: compactLandscape ? 0.75 : { xs: 0.75, sm: 1 },
              px: compactLandscape ? 0.75 : { xs: 1, sm: 2 },
              py: compactLandscape ? 0.25 : { xs: 0.75, sm: 1 },
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, alignItems: 'center' }}>
              {compactLandscape ? (
                <IconButton
                  color="primary"
                  onClick={handleCompose}
                  disabled={viewMode === 'compose'}
                  aria-label="Compose"
                  sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCompose}
                  disabled={viewMode === 'compose'}
                  sx={{ minWidth: { xs: 'auto', sm: 'unset' } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Compose</Box>
                </Button>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ResponsiveScrollTabs
                tabs={folderTabs}
                value={folder}
                onChange={(value) => handleFolderChange(value as FolderType)}
                selectLabel="Folder"
                ariaLabel="Message folders"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, alignItems: 'center' }}>
              {!isMinor && folder === 'trash' && (
                compactLandscape ? (
                  <IconButton size="small" color="error" onClick={() => setEmptyTrashDialogOpen(true)} aria-label="Empty trash">
                    <DeleteSweepIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => setEmptyTrashDialogOpen(true)}
                    sx={{ minWidth: { xs: 'auto', sm: 'unset' } }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Empty Trash</Box>
                  </Button>
                )
              )}
              {!isMinor && (
                compactLandscape ? (
                  <Tooltip
                    title={`Expire all undelivered messages: $${releasableAmount.toFixed(2)} (postage + undelivered attached funds from ${pendingMessageCount} pending message${pendingMessageCount === 1 ? '' : 's'})`}
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={releasableAmount <= 0}
                        onClick={() => { setReleaseResult(null); setReleaseError(null); setReleaseDialogOpen(true); }}
                        aria-label={`Expire all message reserves $${releasableAmount.toFixed(2)}`}
                      >
                        <LockOpenIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<LockOpenIcon />}
                      disabled={releasableAmount <= 0}
                      onClick={() => { setReleaseResult(null); setReleaseError(null); setReleaseDialogOpen(true); }}
                      sx={{ minWidth: { xs: 'auto', sm: 'unset' }, whiteSpace: 'nowrap' }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Expire All</Box>
                      <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Expire</Box>
                      <Tooltip title="Expires undelivered messages and returns their reserved postage and attached funds to your available balance.">
                        <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'primary.main' }} />
                      </Tooltip>
                    </Button>
                    <Typography
                      variant="body2"
                      color={releasableAmount > 0 ? 'primary.main' : 'text.secondary'}
                      sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      ${releasableAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </Paper>

        {/* Main Content Area */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {viewMode === 'list' && (
            <MessageList
              key={`${folder}-${refreshKey}`}
              folder={folder}
              onMessageSelect={handleMessageSelect}
              compactLandscape={compactLandscape}
            />
          )}

          {viewMode === 'view' && selectedMailId && (
            <MessageViewer
              mailId={selectedMailId}
              folder={folder}
              onBack={handleBack}
              onReply={handleReply}
              onReplyAll={handleReplyAll}
              onForward={handleForward}
              onDelete={isMinor ? undefined : handleDelete}
            />
          )}

          {viewMode === 'compose' && (
            <MessageComposer
              onClose={handleBack}
              onSent={handleSent}
              onDraftSaved={handleDraftSaved}
              replyTo={replyTo}
              composeMode={composeMode}
              initialDraft={selectedDraft}
              initialRecipients={initialComposeRecipients}
            />
          )}
        </Box>
      </Box>

      {/* Empty Trash Confirmation Dialog */}
      <Dialog open={emptyTrashDialogOpen} onClose={() => setEmptyTrashDialogOpen(false)}>
        <DialogTitle>Empty Trash</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete all messages in the trash? This cannot be undone.
            <br /><br />
            <strong>Note:</strong> Transaction records in Account History will be preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmptyTrashDialogOpen(false)} disabled={emptyTrashLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleEmptyTrash} 
            color="error" 
            variant="contained"
            disabled={emptyTrashLoading}
          >
            {emptyTrashLoading ? <CircularProgress size={20} /> : 'Empty Trash'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release message reserves confirmation */}
      <Dialog
        open={releaseDialogOpen}
        onClose={() => { if (!releaseLoading) { setReleaseDialogOpen(false); setReleaseResult(null); setReleaseError(null); } }}
      >
        <DialogTitle>
          {releaseResult ? 'Messages Expired' : 'Expire All Undelivered Messages'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {releaseResult ? (
              <>
                Successfully expired {releaseResult.expiredCount} message(s).
                <br />
                Released ${releaseResult.releasedAmount.toFixed(2)} back to your available balance.
              </>
            ) : (
              <>
                This will expire <strong>all undelivered messages</strong> and return their reserved
                postage and attached funds to your available balance.
                <br /><br />
                <strong>Amount to release: ${releasableAmount.toFixed(2)}</strong>
                {pendingMessageCount > 0 && (
                  <> ({pendingMessageCount} pending message{pendingMessageCount === 1 ? '' : 's'})</>
                )}
                <br /><br />
                • Messages that have been read by some recipients will remain delivered to them
                <br />
                • Messages that have not been read by any recipient will be fully canceled
                <br />
                • Reserved postage and undelivered attached funds will be released
                <br /><br />
                This action cannot be undone.
                {releaseError && (
                  <>
                    <br /><br />
                    <Typography color="error" component="span">{releaseError}</Typography>
                  </>
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {releaseResult ? (
            <Button onClick={() => { setReleaseDialogOpen(false); setReleaseResult(null); }} color="primary">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={() => setReleaseDialogOpen(false)} disabled={releaseLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleReleaseReserves}
                color="primary"
                variant="contained"
                disabled={releaseLoading || releasableAmount <= 0}
              >
                {releaseLoading ? <CircularProgress size={20} /> : `Expire All $${releasableAmount.toFixed(2)}`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;

